import { useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../../auth/useAuth.ts';
import { Modal } from '../../ui/Modal.tsx';

interface AppMenuProps {
  /**
   * Opens the invite panel. Absent when there is no group, and when the caller may not
   * invite into it — offering it would only lead to a form that always fails.
   */
  onOpenInvite?: (() => void) | undefined;
  /**
   * Opens the group's lists. Absent until there is a group — a menu entry that opens
   * nothing is worse than no entry.
   */
  onOpenLists?: (() => void) | undefined;
  /** Which list, or how many. Two entries named for nouns need to say *which* noun. */
  listsHint?: string | undefined;
  /** Opens the group panel. Absent on the same terms as the lists. */
  onOpenGroups?: (() => void) | undefined;
  /** The group being shown, so the entry says whose people and name it would open. */
  groupsHint?: string | undefined;
}

/**
 * Everything that is not shopping, behind one icon.
 *
 * The corner is the only place a menu can go without taking width from the group name,
 * but it is also the hardest part of a phone screen to reach — so the *choices* open as a
 * sheet at the bottom, where a thumb already is. Opening it is one deliberate stretch;
 * picking from it is not.
 */
export function AppMenu({
  onOpenInvite,
  onOpenLists,
  listsHint,
  onOpenGroups,
  groupsHint,
}: AppMenuProps) {
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        className="icon-button"
        // The sheet is a dialog, not a listbox — say so, so a screen reader announces
        // what the button is about to do.
        aria-haspopup="dialog"
        aria-label="Menu"
        onClick={() => setIsOpen(true)}
      >
        <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" focusable="false">
          <path
            d="M4 7h16M4 12h16M4 17h16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {isOpen && (
        <Modal title="Menu" variant="sheet" onClose={() => setIsOpen(false)}>
          <div className="menu">
            <Link className="menu__item" to="/profile" onClick={() => setIsOpen(false)}>
              <span className="menu__label">Profile</span>
              <span className="menu__hint">{user?.displayName}</span>
            </Link>

            {/* First of the admin entries, and its own rather than a section of the group
                panel it used to open inside: it is the one piece of admin with a moment
                attached to it — someone is standing there asking to be added. */}
            {onOpenInvite && (
              <button
                type="button"
                className="menu__item"
                onClick={() => {
                  setIsOpen(false);
                  onOpenInvite();
                }}
              >
                <span className="menu__label">Invite</span>
                <span className="menu__hint">Add someone to this group</span>
              </button>
            )}

            {/* Named for the two nouns the switchers are named for, rather than one
                "Settings" holding both. Plural on purpose: each panel is about the
                group's lists (or your groups), one of which happens to be showing —
                which is what makes "new list" belong inside it rather than nowhere. */}
            {onOpenLists && (
              <button
                type="button"
                className="menu__item"
                onClick={() => {
                  setIsOpen(false);
                  onOpenLists();
                }}
              >
                <span className="menu__label">Lists</span>
                {listsHint && <span className="menu__hint">{listsHint}</span>}
              </button>
            )}

            {onOpenGroups && (
              <button
                type="button"
                className="menu__item"
                onClick={() => {
                  setIsOpen(false);
                  onOpenGroups();
                }}
              >
                <span className="menu__label">Groups</span>
                {groupsHint && <span className="menu__hint">{groupsHint}</span>}
              </button>
            )}

            <button type="button" className="menu__item menu__item--danger" onClick={signOut}>
              <span className="menu__label">Sign out</span>
              <span className="menu__hint">{user?.email}</span>
            </button>

            {/* Tapping the backdrop and pressing Escape both work, but neither is
                visible. On a phone this is the row a thumb finds first. */}
            <button
              type="button"
              className="menu__item menu__item--quiet"
              onClick={() => setIsOpen(false)}
            >
              <span className="menu__label">Close</span>
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
